import { LitElement, html, nothing } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../shared/services/debug.service.js';
import { style } from './step-communication-v2.style.js';
import { ApplicationService } from '../../../services/application.service.js';
import '../step-name/name/name.component.js';
import '../step-name/toggle-coborrower/toggle-coborrower.component.js';
import './communication-v2/communication-v2.component.js';
import { globalStyle } from '../../../global-styles.style.js';
import { REFI_PERSONAL_CONST } from '../../../shared/constants/app.constants.js';
import { validateForm } from '../../../shared/decorators/validate-form.decorator.js';
import { stratum } from '../../../../constants/stratum.js';
import { dobFormatter } from '../../../shared/formatters/dob.formatter.js';
import { phoneNumberFormatter } from '../../../shared/formatters/phoneNumber.formatter.js';

class RefiStepCommunicationV2Component extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepCommunicationV2');
    this.validity = {};
    this.useTextOptIn = false;
    this.childIds = {
      primaryForm: 'primary-form',
      coappForm: 'coapp-form',
    };
    this.isPrefill = false;
    this.cardOpen = false;
    this.pending = true;
    this.submitting = false;
    this.pending = false;
    this.coEditCardTagFired = false;
  }

  static properties = {
    application: { type: Object, reflect: true },
    isJointApplication: { type: Boolean, reflect: true },
    cardOpen: { type: Boolean, reflect: true },
    isPrefill: { type: Boolean, attribute: 'is-prefill' },
    pending: { type: Boolean, attribute: 'pending', reflect: true },
  };

  static styles = [style, globalStyle];

  connectedCallback() {
    super.connectedCallback();
    this.isJointApplication =
      ApplicationService.Application?.applicationType === 'JOINT';

    // Set isPrefill to false (don't show card) if any data is missing from applicant name, email, phone, dob, ssn

    if (
      ApplicationService.Application.profileType ===
        REFI_PERSONAL_CONST.FINANCIAL &&
      ApplicationService.Application.primaryApplicant.firstName &&
      ApplicationService.Application.primaryApplicant.lastName &&
      ApplicationService.Application.primaryApplicant.emailAddress &&
      ApplicationService.Application.primaryApplicant.phoneNumber &&
      ApplicationService.Application.primaryApplicant.dateOfBirth &&
      (ApplicationService.Application.primaryApplicant.lastFourTaxId ||
        ApplicationService.Application.primaryApplicant.ssn)
    ) {
      this.isPrefill = true;
    }

    const applicant = {
      firstName: ApplicationService.Application.primaryApplicant.firstName,
      lastName: ApplicationService.Application.primaryApplicant.lastName,
      emailAddress:
        ApplicationService.Application.primaryApplicant.emailAddress,
      phoneNumber: phoneNumberFormatter(
        ApplicationService.Application.primaryApplicant.phoneNumber
      ),
      dateOfBirth: dobFormatter(
        ApplicationService.Application.primaryApplicant.dateOfBirth
      ),
      lastFourTaxId:
        ApplicationService.Application.primaryApplicant.lastFourTaxId,
    };
    this.firePrefilledFieldTag(applicant);

    stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL);

    // Setting pending to false after a short delay to prevent double clicks from progressing the form further
    setTimeout(() => {
      this.pending = false;
    }, 200);
  }

  firePrefilledFieldTag(applicant) {
    for (const key in applicant) {
      // eslint-disable-next-line no-prototype-builtins
      if (applicant.hasOwnProperty(key)) {
        const value = applicant[key];
        if (
          value !== '' &&
          value !== null &&
          value !== undefined &&
          value !== 0
        ) {
          stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_PREFILLED_FIELD, {
            replacements: {
              value: key === 'lastFourTaxId' ? `ssn` : `${key}`,
            },
          });
        }
      }
    }
  }

  render() {
    return html`
      <form
        id="step-communication-v2-form"
        class="step-communication-v2-form"
        @submit="${this.onSubmit}"
        novalidate
      >
        <refi-heading is-iconic>
          ${this.isPrefill
            ? html`Let’s review your info`
            : html`Let’s complete your info`}
        </refi-heading>

        <refi-personal-communication
          id="${this.childIds.primaryForm}"
          compact
          ?is-prefill="${this.isPrefill}"
          @validity="${this.updateValidity}"
          @edit-card-closed="${this.editCardClosed}"
          @edit-card-open="${this.editCardOpened}"
          @pending="${this.updatePending}"
          applicant-type="primaryApplicant"
          ?is-joint-application="${this.isJointApplication}"
          ?is-communication-step-v2=${true}
        ></refi-personal-communication>

        <refi-toggle-coborrower
          ?is-iconic=${true}
          @toggle-coborrower="${this.toggleCoborrower}"
        ></refi-toggle-coborrower>

        ${this.isJointApplication
          ? html` <div class="coborrower-card">
              <div class="coborrower-card-content">
                <fs-heading size="normal" level="2" variant="primary">
                  Co-borrower personal
                </fs-heading>
                <refi-personal-communication
                  id="${this.childIds.coappForm}"
                  compact
                  ?is-prefill="${this.isPrefill}"
                  @validity="${this.updateValidity}"
                  applicant-type="coApplicant"
                  @pending="${this.updatePending}"
                  @ssn-updated="${this.checkCoappSsn}"
                  ?is-communication-step-v2=${true}
                >
                </refi-personal-communication>
              </div>
            </div>`
          : nothing}

        <fs-button class="fs-button next-button" variant="primary"
          >Next
        </fs-button>
      </form>
    `;
  }

  editCardOpened() {
    this.cardOpen = true;
  }

  editCardClosed() {
    this.cardOpen = false;
  }

  updateValidity(ev) {
    this.validity[ev.detail.name] = ev.detail.isValid;
  }

  updatePending(ev) {
    this.pending = ev.detail;
    this.debug.log('Pending', this.pending);
  }

  checkCoappSsn(ev) {
    // Ensure that primary and coapplicant SSNs are not the same. This needs to be done if in the nonfinancial flow because data is not saved to application yet
    if (this.isJointApplication) {
      this.pending = true;
      const coAppForm = this.shadowRoot.getElementById(this.childIds.coappForm);
      const primaryForm = this.shadowRoot.getElementById(
        this.childIds.primaryForm
      );

      if (coAppForm) {
        const coAppVal = coAppForm.shadowRoot.getElementById(
          coAppForm.childIds.ssn
        )?.values.ssn;
        const primaryAppVal = primaryForm.shadowRoot.getElementById(
          primaryForm.childIds.ssn
        )?.values.ssn;

        const ssnMatch =
          !primaryAppVal || !coAppVal ? false : coAppVal === primaryAppVal;

        this.validity.compareSsns = !ssnMatch;

        if (ssnMatch) {
          const fieldToAlert = ev.detail.isPrimary ? primaryForm : coAppForm;
          fieldToAlert.shadowRoot
            .getElementById(fieldToAlert.childIds.ssn)
            .setSsnMatch(true);
        } else {
          primaryForm.shadowRoot
            .getElementById(primaryForm.childIds.ssn)
            .setSsnMatch(false);
          coAppForm.shadowRoot
            .getElementById(coAppForm.childIds.ssn)
            .setSsnMatch(false);
        }
      }
    } else {
      this.validity.compareSsns = true;
    }
    this.pending = false;
    this.requestUpdate();
    // }
  }

  @validateForm()
  async onSubmit() {
    this.checkCoappSsn({ detail: { isPrimary: false } });

    if (this.pending) {
      this.debug.log('Pending');
      return;
    }

    this.pending = true;
    this.debug.log('Checking validity', this.validity);

    this.primaryApplicantCard = this.shadowRoot.getElementById(
      this.childIds.primaryForm
    );

    if (this.cardOpen) {
      this.primaryApplicantCard.showEditError();
      return;
    }

    await this.runChildrenBusinessChecks().then(() => {
      this.debug.log('Validity after business checks', this.validity);
      let valid = true;

      if (!this.isJointApplication) {
        Object.keys(this.validity)
          .filter((key) => !key.startsWith('co-'))
          .forEach((key) => {
            if (!this.validity[key]) {
              valid = false;
            }
          });
      } else {
        Object.values(this.validity).forEach((validityValue) => {
          if (!validityValue) {
            valid = false;
          }
        });
      }
      this.debug.log(this.validity);

      if (valid) {
        this.oldApplicantInfo = structuredClone(
          ApplicationService.Application.primaryApplicant
        );
        if (this.isJointApplication) {
          this.oldCoApplicantInfo = structuredClone(
            ApplicationService.Application.coApplicant
          );
        }
        this.saveInfoToApplicant();
        this.debug.log('valid', ApplicationService.Application);

        this.debug.log(
          'Updating application in host app in case application type has changed'
        );
        this.dispatchEvent(
          new CustomEvent('host-app-update-application', {
            bubbles: true,
            composed: true,
            detail: {
              data: { application: ApplicationService.Application },
            },
          })
        );

        this.dispatchEvent(new CustomEvent('next-step'));
        stratum.publishTag(TagKey.PREQUALIFY_NEXT_PAGE_CTA, {
          replacements: { value: 'personal' },
        });
      }
    });

    setTimeout(() => {
      this.pending = false;
    }, 200);
  }

  toggleCoborrower(ev) {
    // eslint-disable-next-line no-constant-binary-expression
    this.isJointApplication = ev.detail.applicationType === 'JOINT' ?? false;
    if (this.isJointApplication && !this.coEditCardTagFired) {
      this.coEditCardTagFired = true;
      stratum.publishTag(TagKey.VIEW_COBORROWER_PERSONAL_EDIT);
    }
    if (!this.isJointApplication) {
      this.coEditCardTagFired = false;
    }
  }

  saveInfoToApplicant() {
    this.primaryApplicantCard = this.shadowRoot.getElementById(
      this.childIds.primaryForm
    );
    this.coApplicantCard = this.shadowRoot.getElementById(
      this.childIds.coappForm
    );

    if (this.primaryApplicantCard) {
      this.primaryApplicantCard.saveInfoToApplicant();
      ApplicationService.primaryApplicantEmailChanged = false;
    }
    if (this.coApplicantCard) {
      this.coApplicantCard.saveInfoToApplicant();
      ApplicationService.coApplicantEmailChanged = false;
    }

    // Update application type
    ApplicationService.Application = {
      ...ApplicationService.Application,
      applicationType: this.isJointApplication ? 'JOINT' : 'INDIVIDUAL',
    };

    // Clear co-applicant if needed using service method
    if (!this.isJointApplication) {
      ApplicationService.clearCoapplicant();
      this.tagFieldChangesOnSubmit(
        ApplicationService.Application.primaryApplicant,
        false
      );
    } else {
      this.tagFieldChangesOnSubmit(
        ApplicationService.Application.primaryApplicant,
        false
      );
      this.tagFieldChangesOnSubmit(
        ApplicationService.Application.coApplicant,
        true
      );
    }
    stratum.publishTag(TagKey.PERSONAL_PAGE_CO_BORROWER_ADDED, {
      replacements: {
        value:
          ApplicationService.Application.applicationType ===
          REFI_PERSONAL_CONST.APPLICATION_TYPE.JOINT
            ? 'yes'
            : 'no',
      },
    });
  }

  tagFieldChangesOnSubmit(applicant, coApp = false) {
    if (coApp) {
      Object.keys(applicant).forEach((field) => {
        if (
          typeof applicant[field] !== 'object' &&
          applicant[field] !== this.oldCoApplicantInfo[field]
        ) {
          this.debug.log(`co-${field}`);
          stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
            replacements: {
              value: `co-${field}`,
            },
          });
        }
      });
    } else {
      Object.keys(applicant).forEach((field) => {
        if (
          typeof applicant[field] !== 'object' &&
          applicant[field] !== this.oldApplicantInfo[field]
        ) {
          this.debug.log(`${field}`);
          stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
            replacements: {
              value: `${field}`,
            },
          });
        }
      });
    }
  }

  async runChildrenBusinessChecks() {
    this.formChildren = {
      primaryApplicant: this.shadowRoot.getElementById(
        this.childIds.primaryForm
      ),
      coApplicant: this.shadowRoot.getElementById(this.childIds.coappForm),
    };
    this.checkCoappSsn({ detail: { isPrimary: false } });

    return this.formChildren.primaryApplicant
      .runChildrenBusinessChecks()
      .then(() => {
        if (this.formChildren.coApplicant) {
          return this.formChildren.coApplicant
            ?.runChildrenBusinessChecks()
            .then(() => Promise.resolve());
        }
        return Promise.resolve();
      });
  }
}

customElements.define(
  'refi-step-communication-v2',
  RefiStepCommunicationV2Component
);
